import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-industries',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './industries.component.html',
  styleUrls: ['./industries.component.css']
})
export class IndustriesComponent {
  selectedIndustry: string = 'rental';

  industries = [
    {
      id: 'rental',
      icon: 'car_rental',
      name: 'Vehicle Rental',
      headline: 'Fleet Management at Scale',
      description: "ZAD ERP's TransArchitect module is purpose-built for vehicle rental businesses. From single-branch operations to multi-city fleets, every workflow is automated for maximum efficiency and minimum downtime.",
      link: '/rental',
      linkLabel: 'Explore TransArchitect',
      stats: [
        { value: '40%', label: 'Reduction in fleet idle time' },
        { value: '3x', label: 'Faster contract processing' },
        { value: '99%', label: 'Contract accuracy rate' }
      ],
      capabilities: [
        { icon: 'calendar_month', text: 'Real-time availability calendar and booking engine' },
        { icon: 'description', text: 'Automated rental contract generation and digital signature' },
        { icon: 'build', text: 'Preventive maintenance scheduling based on mileage or time' },
        { icon: 'payments', text: 'Integrated billing, deposits, and late-return penalties' },
        { icon: 'person_search', text: 'Customer KYC and driver verification workflow' },
        { icon: 'analytics', text: 'Fleet utilization reports and revenue analytics' }
      ]
    },
    {
      id: 'manufacturing',
      icon: 'factory',
      name: 'Manufacturing',
      headline: 'Production & Supply Chain Intelligence',
      description: 'Manage raw materials, production runs, and finished goods with a system that integrates the shop floor directly with your finance and inventory modules for complete operational visibility.',
      link: null,
      linkLabel: null,
      stats: [
        { value: '28%', label: 'Reduction in material waste' },
        { value: '2x', label: 'Faster production planning' },
        { value: '100%', label: 'Real-time inventory visibility' }
      ],
      capabilities: [
        { icon: 'precision_manufacturing', text: 'Bill of Materials (BOM) and production order management' },
        { icon: 'inventory_2', text: 'Raw material tracking with automated reorder triggers' },
        { icon: 'conveyor_belt', text: 'Work-in-progress monitoring across production stages' },
        { icon: 'quality_control', text: 'Quality control checkpoints and defect logging' },
        { icon: 'local_shipping', text: 'Supplier management and purchase order automation' },
        { icon: 'bar_chart', text: 'Production cost analysis and margin reporting' }
      ]
    },
    {
      id: 'retail',
      icon: 'shopping_bag',
      name: 'Retail & Commerce',
      headline: 'Omni-Channel Retail Operations',
      description: 'Unify your in-store, online, and wholesale operations under one system. From point-of-sale to customer loyalty programs, ZAD ERP gives retail businesses the tools to grow intelligently.',
      link: null,
      linkLabel: null,
      stats: [
        { value: '35%', label: 'Increase in inventory accuracy' },
        { value: '22%', label: 'Reduction in stockouts' },
        { value: '4x', label: 'Faster year-end reconciliation' }
      ],
      capabilities: [
        { icon: 'point_of_sale', text: 'Point-of-sale integration with real-time stock sync' },
        { icon: 'loyalty', text: 'Customer loyalty and discount program management' },
        { icon: 'storefront', text: 'Multi-branch retail operations from a single dashboard' },
        { icon: 'package_2', text: 'Automatic stock replenishment and supplier ordering' },
        { icon: 'receipt_long', text: 'Sales reporting by branch, category, and time period' },
        { icon: 'manage_accounts', text: 'Customer account management and purchase history' }
      ]
    },
    {
      id: 'real-estate',
      icon: 'domain',
      name: 'Real Estate',
      headline: 'Property Portfolio Management',
      description: 'From property listings to tenant contracts and maintenance requests, ZAD ERP provides real estate companies with a structured, automated approach to managing complex portfolios.',
      link: null,
      linkLabel: null,
      stats: [
        { value: '50%', label: 'Less time on contract admin' },
        { value: '30%', label: 'Improvement in rent collection' },
        { value: '100%', label: 'Lease lifecycle visibility' }
      ],
      capabilities: [
        { icon: 'apartment', text: 'Property and unit management across multiple portfolios' },
        { icon: 'contract', text: 'Lease agreement lifecycle from draft to expiry' },
        { icon: 'request_quote', text: 'Automated rent invoicing and collection tracking' },
        { icon: 'handyman', text: 'Maintenance request tracking and vendor assignment' },
        { icon: 'people', text: 'Tenant communication and portal access' },
        { icon: 'trending_up', text: 'Occupancy rate and yield analytics dashboards' }
      ]
    },
    {
      id: 'transportation',
      icon: 'local_shipping',
      name: 'Transportation & Logistics',
      headline: 'End-to-End Logistics Orchestration',
      description: 'Built for freight companies, delivery services, and transport operators, TransArchitect powers every stage of logistics from route planning to invoicing and fleet maintenance.',
      link: '/transarchitect',
      linkLabel: 'Explore TransArchitect',
      stats: [
        { value: '25%', label: 'Fuel cost reduction' },
        { value: '45%', label: 'Faster invoice-to-cash cycle' },
        { value: '98%', label: 'On-time delivery rate' }
      ],
      capabilities: [
        { icon: 'route', text: 'Route planning and optimization for multi-stop deliveries' },
        { icon: 'local_shipping', text: 'Fleet tracking and maintenance scheduling' },
        { icon: 'receipt', text: 'Automated freight invoicing and billing' },
        { icon: 'group', text: 'Driver management, licensing, and performance tracking' },
        { icon: 'package', text: 'Shipment tracking and customer notification workflows' },
        { icon: 'bar_chart', text: 'Profitability analysis by route, driver, and vehicle' }
      ]
    }
  ];

  get selectedIndustryData() {
    return this.industries.find(i => i.id === this.selectedIndustry) || this.industries[0];
  }
}
